// src/services/api/transaction.service.js

// Import the configured apiClient
import { apiClient } from "../../config/api.config";

// Remove direct axios import and the BASE_URL constant
// import axios from "axios";
import { API_BASE_URL } from "../../config/constants";

// Remove the getHeaders function as apiClient handles it
// const getHeaders = () => {
//   const token = localStorage.getItem("authToken");
//   if (!token) {
//     throw new Error("No authentication token found. Please log in.");
//   }
//   return {
//     headers: {
//       "Content-Type": "application/json", // Content-Type is often handled by Axios by default for JSON bodies
//       Authorization: `Bearer ${token}`,
//     },
//   };
// };

// Helper function to get current user ID from token
// This function doesn't depend on axios or API_BASE_URL, so it can remain
const getCurrentUserId = () => {
  const token = localStorage.getItem("authToken");
  if (!token) {
    return null;
  }

  try {
    // Extract payload from JWT token
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(window.atob(base64));

    // Return user ID from token (check common claim names)
    return payload.userId || payload.id || payload.sub;
  } catch (error) {
    return null;
  }
};

// Handle API errors consistently
// This function provides a custom return format, so it is kept
const handleApiError = (error, defaultMessage) => {

  // Check if the error is an Axios error with a response
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    // Use optional chaining for safety
    return {
      success: false,
      error:
        error.response.data?.message ||
        error.response.data?.error ||
        defaultMessage,
      status: error.response.status,
    };
  } else if (error.request) {
    // The request was made but no response was received
    return {
      success: false,
      error: "No response received from server",
    };
  } else {
    // Something happened in setting up the request that triggered an Error
    return {
      success: false,
      error: error.message || defaultMessage,
    };
  }
};

// API service for transaction-related operations
const TransactionService = {
  /**
   * Get wallet balance and recent transactions
   * @returns {Promise} Promise with wallet balance data ({ success, data, error, status })
   */
  getWalletBalance: async () => {
    // Use apiClient instead of axios
    // Remove the manual getHeaders() call
    try {
      const response = await apiClient.get(
        "/transactions/wallet" // Use relative path
        // No need for headers here, apiClient handles it
      );
      const walletData = response.data;

      return {
        success: true,
        data: walletData,
      };
    } catch (error) {
      return handleApiError(error, "Failed to fetch wallet balance");
    }
  },

  /**
   * Initialize a wallet for the user
   * @returns {Promise} Promise with the created wallet data ({ success, data, error, status })
   */
  createWallet: async () => {
    // Use apiClient instead of axios
    // Remove the manual getHeaders() call
    try {
      const response = await apiClient.post(
        "/transactions/wallet", // Use relative path
        {} // Empty body for POST request
        // No need for headers here, apiClient handles it
      );

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, "Failed to create wallet");
    }
  },

  /**
   * Initiate a deposit to the wallet
   * @param {number} amount - Amount to deposit
   * @returns {Promise} Promise with deposit initiation data ({ success, data, error, status })
   */
  initiateDeposit: async (amount) => {
    try {
      if (!amount || isNaN(amount) || amount <= 0) {
        return {
          success: false,
          error: "Please provide a valid deposit amount",
        };
      }

      // Use apiClient instead of axios
      // Remove the manual getHeaders() call
      const response = await apiClient.post(
        "/transactions/deposit", // Use relative path
        { amount }
        // No need for headers here, apiClient handles it
      );

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      // If wallet not found (404), try creating one and then deposit again
      if (error.response?.status === 404) {
        try {
          // Use the refactored createWallet method
          const createResponse = await TransactionService.createWallet();

          if (createResponse.success) {
            // Try deposit again using the refactored apiClient
            const depositResponse = await apiClient.post(
              "/transactions/deposit", // Use relative path
              { amount }
              // No need for headers here, apiClient handles it
            );

            return {
              success: true,
              data: depositResponse.data,
            };
          } else {
            // If wallet creation failed, return that error
            return createResponse; // createResponse already has success: false and error details
          }
        } catch (createError) {
          // Handle potential errors during the creation attempt itself
          return handleApiError(
            createError,
            "Error creating wallet during deposit"
          );
        }
      }

      // If error is not 404, handle the original deposit error
      return handleApiError(error, "Failed to initiate deposit");
    }
  },
  /**
   * Cancel a pending payment/deposit
   * @param {Object} cancelData - Data required for cancellation
   * @param {string} cancelData.razorpayOrderId - Razorpay order ID to cancel
   * @returns {Promise} Promise with cancellation result ({ success, data, error, status })
   */
  // TransactionService.js improvement
  cancelPayment: async (cancelData) => {
    if (!cancelData?.razorpayOrderId) {
      return {
        success: false,
        error: "Razorpay order ID is required for cancellation",
      };
    }

    try {
      // Add cancellation source for better tracking
      const enhancedData = {
        ...cancelData,
        cancellationReason: cancelData.reason || "User cancelled payment flow",
      };

      const response = await apiClient.post(
        "/transactions/cancel-payment",
        enhancedData
      );

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {

      // Even if API fails, tell user it's cancelled to avoid confusion
      return {
        success: false,
        error: handleApiError(error, "Failed to record payment cancellation")
          .error,
        uiSuccess: true, // Special flag so UI can still show cancelled message
      };
    }
  },

  /**
   * Verify payment after successful transaction
   * @param {Object} paymentData - Payment verification data
   * @returns {Promise} Promise with payment verification result ({ success, data, error, status })
   */
  verifyPayment: async (paymentData) => {
    // Use apiClient instead of axios
    // Remove the manual getHeaders() call
    try {
      const response = await apiClient.post(
        "/transactions/verify-payment", // Use relative path
        paymentData
        // No need for headers here, apiClient handles it
      );

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, "Failed to verify payment");
    }
  },

  /**
   * Get withdrawal status
   * @param {string} withdrawalId - ID of the withdrawal transaction
   * @returns {Promise} Promise with withdrawal status data ({ success, data, error, status })
   */
  getWithdrawalStatus: async (withdrawalId) => {
    // Use apiClient instead of axios
    // Remove the manual getHeaders() call
    try {
      const response = await apiClient.get(
        `/transactions/withdraw/status/${withdrawalId}` // Use relative path
        // No need for headers here, apiClient handles it
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, "Failed to fetch withdrawal status");
    }
  },

  /**
   * Get transaction history with optional filters
   * @param {Object} filters - Optional filters for transactions
   * @returns {Promise} Promise with transaction history data ({ success, data, error, status })
   */
  getTransactionHistory: async (filters = {}) => {
    // Use apiClient instead of axios
    // Remove the manual getHeaders() call
    try {
      const response = await apiClient.get(
        "/transactions/history", // Use relative path
        {
          // No need for headers here, apiClient handles it
          params: filters, // Pass filters as params
        }
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, "Failed to fetch transaction history");
    }
  },

  /**
   * Generate transaction report
   * Note: This endpoint seems to be an admin endpoint based on the URL '/admin/users/transaction-report'.
   * If this is intended for regular users, ensure your backend permissions allow it.
   * @param {Object} reportData - Report generation parameters
   * @returns {Promise} Promise with report generation result ({ success, data, error, status })
   */
  generateReport: async (reportData) => {
    try {
      // Get user ID from auth utility (still needed if backend requires it in payload or URL)
      // Based on the endpoint, it seems the backend might not need the userId *in the frontend logic*
      // if it's an admin endpoint filtering by a user ID passed in reportData,
      // or if the admin context determines the user.
      // If the backend DOES need the user ID from the logged-in user's token,
      // the apiClient interceptor handles the auth header.
      // The original code fetched it but didn't seem to use it in the request body/params,
      // but kept it for clarity. Keeping the function call but removing the unused variable.
      const userId = getCurrentUserId(); // Keep this if needed elsewhere or for logic, but not used in the API call below based on original code

      if (!userId) {
        // This check relies on getCurrentUserId working
        // However, the apiClient intercepts authentication errors, so this manual check
        // might be redundant if the backend strictly enforces auth.
        // Keeping it for now to match original logic flow.
        return {
          success: false,
          error: "User not authenticated or token invalid",
        };
      }

      // Use apiClient instead of axios
      // Remove the manual getHeaders() call
      // Corrected endpoint relative path based on baseURL /api/v1 and report routes mount point /reports
      const response = await apiClient.post(
        "/reports/admin/users/transaction-report",
        reportData
        // No need for headers here, apiClient handles it
      );



      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, "Failed to generate report");
    }
  },

  /**
   * Get the status of a report generation request
   * @param {string} reportId - ID of the report to check
   * @returns {Promise} Promise with report status data ({ success, data, error, status })
   */
  getReportStatus: async (reportId) => {
    // Use apiClient instead of axios
    // Remove the manual getHeaders() call
    try {
      const response = await apiClient.get(
        `/reports/admin/user-report/${reportId}` // Use relative path matching backend route
        // No need for headers here, apiClient handles it
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, "Failed to fetch report status");
    }
  },

  /**
   * Download a generated report
   * @param {string} reportId - ID of the report to download
   * @returns {Promise} Promise with report download data ({ success, headers, data, error, status })
   */
  downloadReport: async (reportId) => {
    // Use apiClient instead of axios
    // Remove the manual headers extraction and passing
    try {
      const response = await apiClient.get(
        `/reports/admin/reports/${reportId}/download`, // Use relative path matching backend route
        {
          // No need for headers property here, apiClient handles it
          responseType: "blob", // Important for file downloads
        }
      );

      return {
        success: true,
        headers: response.headers, // Return headers for filename or content type
        data: response.data, // The blob data
      };
    } catch (error) {
      return handleApiError(error, "Failed to download report");
    }
  },

  /**
   * Get all transactions (admin only) with pagination and filtering
   * @param {Object} params - Query parameters for fetching transactions
   * @returns {Promise} Promise with transaction list data ({ success, data, error, status })
   */
  getAllTransactions: async (params = {}) => {
    // Use apiClient instead of axios
    // Remove the manual getHeaders() call
    try {
      const response = await apiClient.get(
        "/admin/transactions", // Use relative path
        {
          // No need for headers here, apiClient handles it
          params, // Pass filters as params
        }
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, "Failed to fetch transactions");
    }
  },

  /**
   * Export transactions to a file
   * @param {Object} filters - Filters for transactions to export
   * @returns {Promise} Promise with exported file data ({ success, headers, data, error, status })
   */
  exportTransactions: async (filters = {}) => {
    // Use apiClient instead of axios
    // Remove the manual headers extraction and passing
    try {
      const response = await apiClient.get(
        "/transactions/export", // Use relative path
        {
          // No need for headers property here, apiClient handles it
          params: filters, // Pass filters as params
          responseType: "blob", // Important for file downloads
        }
      );

      return {
        success: true,
        headers: response.headers, // Return headers for filename or content type
        data: response.data, // The blob data
      };
    } catch (error) {
      return handleApiError(error, "Failed to export transactions");
    }
  },

  /**
   * Initiate a withdrawal from the wallet with dynamic method (UPI or BANK)
   * @param {Object} payload - Withdrawal request data
   * @param {number} payload.amount - Amount to withdraw
   * @param {string} payload.withdrawalMethod - 'UPI' or 'BANK'
   * @param {string} [payload.upiId] - Required if withdrawalMethod is 'UPI'
   * @param {Object} [payload.bankAccount] - Required if withdrawalMethod is 'BANK'
   * @param {string} payload.bankAccount.accountNumber
   * @param {string} payload.bankAccount.bankName
   * @param {string} payload.bankAccount.ifscCode
   * @param {string} payload.bankAccount.accountHolderName
   * @returns {Promise} Promise with withdrawal initiation data ({ success, data, error, status })
   */
  initiateWithdrawal: async (payload) => {
    try {
      // Basic validation for the payload
      if (
        !payload ||
        !payload.amount ||
        isNaN(payload.amount) ||
        payload.amount <= 0
      ) {
        return {
          success: false,
          error: "Please provide a valid withdrawal amount.",
        };
      }
      if (
        !payload.withdrawalMethod ||
        (payload.withdrawalMethod !== "UPI" &&
          payload.withdrawalMethod !== "BANK")
      ) {
        return {
          success: false,
          error:
            "Invalid withdrawal method specified. Must be 'UPI' or 'BANK'.",
        };
      }

      // Send the entire payload directly to the new endpoint
      const response = await apiClient.post(
        "/transactions/withdrawal-request", // This is the new endpoint
        payload
      );

      return {
        success: true,
        data: response.data, // The response contains requestId, amount, currency, withdrawalMethod, status, requestedAt
      };
    } catch (error) {
      // Assuming handleApiError is defined elsewhere in your service file
      return handleApiError(error, "Failed to process withdrawal request");
    }
  },

  // Get user's withdrawal requests
  getUserWithdrawalRequests: async (page = 1, limit = 20) => {
    try {
      // Basic validation for pagination parameters
      if (isNaN(page) || page < 1) {
        return {
          success: false,
          error: "Please provide a valid page number.",
        };
      }

      if (isNaN(limit) || limit < 1 || limit > 100) {
        return {
          success: false,
          error: "Please provide a valid limit (1-100).",
        };
      }

      // Send request to get user's withdrawal requests
      const response = await apiClient.get(
        `/transactions/withdrawal-requests/my-requests?page=${page}&limit=${limit}`
      );

      return {
        success: true,
        data: response.data, // Contains requests array and pagination info
      };
    } catch (error) {
      // Assuming handleApiError is defined elsewhere in your service file
      return handleApiError(error, "Failed to fetch withdrawal requests");
    }
  },

  /**
   * Cancels a pending withdrawal request.
   * @param {string} requestId The ID of the withdrawal request to cancel.
   * @returns {Promise<{success: boolean, data?: any, error?: string}>} An object indicating the result of the cancellation.
   */
  cancelWithdrawalRequest: async (requestId) => {
    if (
      !requestId ||
      typeof requestId !== "string" ||
      requestId.trim() === ""
    ) {
      return {
        success: false,
        error: "Request ID is required and must be a valid string.",
      };
    }
    try {
      const response = await apiClient.patch(
        `/transactions/withdrawal-requests/${requestId}/cancel`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, "Failed to cancel withdrawal request");
    }
  },

  /**
   * Helper to determine filename from headers or defaults
   */
  getDownloadFilename: (contentDisposition, format, contentType) => {
    let filename;
    // Try to get filename from Content-Disposition header
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Use format-specific filename if not provided in headers
    if (!filename) {
      if (format === 'PDF') {
        filename = 'transaction_report.pdf';
      } else if (format === 'EXCEL') {
        filename = 'transaction_report.xlsx';
      } else {
        filename = 'transaction_report.csv';
      }
    }

    // Ensure correct file extension based on content type
    if (contentType) {
      if (contentType.includes('spreadsheetml') && !filename.endsWith('.xlsx')) {
        filename = filename.replace(/\.[^/.]+$/, '') + '.xlsx';
      } else if (contentType.includes('csv') && !filename.endsWith('.csv')) {
        filename = filename.replace(/\.[^/.]+$/, '') + '.csv';
      } else if (contentType.includes('pdf') && !filename.endsWith('.pdf')) {
        filename = filename.replace(/\.[^/.]+$/, '') + '.pdf';
      }
    }
    return filename;
  },

  /**
   * Helper to trigger browser download
   */
  triggerFileDownload: (blob, filename) => {
    if (!blob) {
      return;
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
};

export default TransactionService;